import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { MagnifyingGlassIcon, XMarkIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { useIsDisabledByPermission } from '../contexts/PermissionAccessContext';
import { useFieldShellDisplay } from '../contexts/DisplayPreferencesContext';
import { useDensity } from '../contexts/DensityContext';
import { Tooltip } from './ui/Tooltip';

/** Widest the dropdown panel may grow to fit long option labels (px). It never
 * shrinks below the input width and is further capped at the viewport width. */
const MAX_PANEL_WIDTH = 480;

// Shared canvas 2D context for measuring whether the closed-field "#id"
// adornment fits beside the value text. Null where canvas isn't implemented
// (jsdom) — callers then keep the adornment.
let measureCanvas: HTMLCanvasElement | null = null;
const getMeasureContext = (): CanvasRenderingContext2D | null => {
  if (!measureCanvas) measureCanvas = document.createElement('canvas');
  try {
    return measureCanvas.getContext('2d');
  } catch {
    return null;
  }
};

/**
 * An option's label, wrapped in a Tooltip that only shows when the text is
 * actually truncated (label longer than the row). Keeps the full value one
 * hover away when the panel can't grow wide enough to show it inline. The
 * truncation check is measured once per label (not per hover) to avoid layout
 * thrash while the highlighted row changes. zIndex sits above the dropdown
 * panel (z-[99999]) so the tooltip isn't hidden behind it.
 */
const OptionLabel: React.FC<{ label: string; className: string }> = ({ label, className }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [truncated, setTruncated] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (el) setTruncated(el.scrollWidth > el.clientWidth + 1);
  }, [label]);
  return (
    <Tooltip content={label} disabled={!truncated} placement="top" delay={150} zIndex={100001}>
      <div ref={ref} className={className}>
        {label}
      </div>
    </Tooltip>
  );
};

export interface TypeAheadOption {
  value: string;
  label: string;
  /** Muted small text rendered inline after the label (e.g. a record's "#recno").
   *  Dropdown display only — never part of the input value or selected chip. */
  labelSuffix?: string;
  /** Resolve-only option: never rendered in the dropdown (excluded from
   *  filtering, pinning, and keyboard navigation) but still consulted for
   *  value→label resolution — the closed input's text, the "#id" adornment,
   *  and the close reconcile. Async value-storing lookups use this to show the
   *  stored record's label without surfacing the current pick as a row the
   *  keyboard highlight could land on. */
  hidden?: boolean;
  /** Listed only once something is typed — for a long tail that would bury the
   *  short list above it (e.g. every user under a handful of groups). Like
   *  `hidden`, it still resolves value→label, so a picked one shows its name. */
  searchOnly?: boolean;
  description?: string;
  icon?: React.ReactNode;
  group?: string;
  /** Arbitrary payload for the caller (e.g. the source row) — surfaced via onSelect. */
  data?: unknown;
}

interface TypeAheadPropsBase {
  options: TypeAheadOption[];
  placeholder?: string;
  label?: string;
  /** aria-label for the input — for fields with no visible `label`. */
  ariaLabel?: string;
  className?: string;
  disabled?: boolean;
  showSearch?: boolean;
  showIcons?: boolean;
  showDescriptions?: boolean;
  groupBy?: boolean;
  maxHeight?: string;
  onClear?: () => void;
  chipsDirection?: 'right' | 'down';
  portalContainer?: HTMLElement | null;
  /** If provided, shows a "+ Add" action in the dropdown. Called with the current search term. */
  onCreateNew?: (searchTerm: string) => void;
  /** Render with reduced padding/font/icons for dense layouts. */
  compact?: boolean;
  /** When true, a single-select value with no matching option is flagged as
   * invalid: the raw value is shown in the input and the input is highlighted
   * orange — an at-a-glance cue that a stored value is no longer a valid choice
   * (e.g. a discontinued reason code). Off by default so options that load
   * async don't briefly flash orange before they arrive. */
  flagInvalidValue?: boolean;
  /** Hide the clear (✕) button when false — for fields that must not be cleared
   * once set. Defaults to true. */
  allowClear?: boolean;
  /** Display text for an invalid (no matching option) single-select value —
   * e.g. the DELETED entity's name resolved by the caller — shown in place of
   * the raw stored value (usually a bare recno). The orange invalid highlight
   * still applies; only used with `flagInvalidValue`. */
  invalidValueLabel?: string;
  /** Force the orange invalid highlight regardless of the options heuristic. The
   *  `flagInvalidValue` path can't serve a `freeText` typeahead (it has no fixed
   *  option list to miss against), so the async FK pickers — which search ACTIVE
   *  records per keystroke — use this to mark a stored value whose target is
   *  deleted or no longer exists. */
  invalid?: boolean;
  /** Free-text mode: muted adornment (e.g. "#4381") shown inside the field's
   * right edge while the dropdown is closed — never part of the input text.
   * Fixed-option mode derives this automatically from the selected option's
   * labelSuffix; free-text callers pass it since TypeAhead can't know the
   * picked record once the option list churns. */
  valueSuffix?: string;
  /** Disable the closed-field "#id" adornment (default on) — for callers that
   * surface the id somewhere roomier instead (e.g. an under-field info line). */
  showValueSuffix?: boolean;
  /** Async/server-search mode: called with the raw search term as the user
   * types, so the caller can fetch and supply `options`. */
  onSearch?: (term: string) => void;
  /** The supplied `options` are already server-filtered for the current term —
   * skip the client-side label/description filter (which would hide matches
   * whose matched column isn't rendered, e.g. a recno or zip search). freeText
   * mode implies this; set it for fixed-option (value-storing) async lookups. */
  serverFiltered?: boolean;
  /** Fires with the full option the user picked (single-select) — use the
   * option's `data` to react to it (e.g. fill a denormalized address). */
  onSelect?: (option: TypeAheadOption) => void;
  /** Fires when the dropdown closes via a click outside / Escape (NOT on an
   * option pick, which closes directly). Lets a caller reconcile free-typed text
   * that wasn't selected (e.g. clear it). */
  onClose?: () => void;
  /** Free-text combobox (single-select): the input mirrors `value` verbatim
   * (not an option label), typing emits onChange(text) + onSearch(text), and a
   * no-match value is NOT flagged invalid. With `onSearch`, provided `options`
   * are shown unfiltered (already server-filtered — for async name/lookup
   * fields); without it, the static list is filtered client-side as you type. */
  freeText?: boolean;
  /** Custom label for the onCreateNew action; receives the current search term.
   * Defaults to `Add "<term>"`. */
  createNewLabel?: (term: string) => string;
  /** When provided, replaces the default label/description for each result with
   * this custom content — e.g. a bill-to/customer detail card shown inline in
   * the dropdown. Return null to fall back to the default rendering. */
  renderOption?: (option: TypeAheadOption) => React.ReactNode;
  /** Async-search in flight: with no options yet, show a "Searching…" spinner
   * instead of the "No results found" empty state (which reads as a definitive
   * miss while the server round-trip is still pending). */
  searching?: boolean;
  /** Replaces the default "No results found" empty state with a plain hint line
   * — e.g. "Type at least 2 characters to search" for async lookups that
   * haven't been given enough input yet. */
  emptyMessage?: string;
  /** Opens the dropdown whenever this number CHANGES (increment it to trigger).
   * For callers that populate `options` from something other than typing — e.g. a
   * "find it for me" button — since freeText mode otherwise only opens on type.
   * Undefined/unchanged leaves open/close behavior exactly as before. */
  openSignal?: number;
  /** Focus the input on mount (e.g. the first field of a freshly-opened builder
   *  row) so the user can type without clicking in first. Mount-only, like the
   *  native attribute. */
  autoFocus?: boolean;
  /** Keep and select a single-select label on focus so it can be copied or
   * replaced by typing. Defaults to clearing the input for a fresh search. */
  preserveValueOnFocus?: boolean;
}

interface TypeAheadPropsSingle extends TypeAheadPropsBase {
  multiple?: false;
  value: string;
  onChange: (value: string) => void;
}

interface TypeAheadPropsMultiple extends TypeAheadPropsBase {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
}

type TypeAheadProps = TypeAheadPropsSingle | TypeAheadPropsMultiple;

const TypeAhead: React.FC<TypeAheadProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Search...',
  label,
  ariaLabel,
  className = '',
  disabled: disabledProp = false,
  showSearch = true,
  showIcons = false,
  showDescriptions = false,
  groupBy = false,
  maxHeight = 'max-h-80',
  onClear,
  multiple = false,
  chipsDirection = 'right',
  portalContainer,
  onCreateNew,
  compact: compactProp,
  flagInvalidValue = false,
  allowClear = true,
  invalidValueLabel,
  invalid: invalidProp = false,
  valueSuffix,
  showValueSuffix = true,
  onSearch,
  serverFiltered = false,
  onSelect,
  onClose,
  freeText = false,
  createNewLabel,
  renderOption,
  searching = false,
  emptyMessage,
  openSignal,
  autoFocus = false,
  preserveValueOnFocus = false,
}) => {
  const compact = useDensity(compactProp);
  const disabledByPermission = useIsDisabledByPermission();
  const disabled = disabledProp || disabledByPermission;
  // Field display prefs: plain => the box, search icon and chevron all go,
  // leaving the selected label as plain text; the class marks this input as the
  // box for the CSS-only options (tint, color, fade).
  const { plain: plainLocked, className: shellClass } = useFieldShellDisplay(disabled);

  // A single-select value with no matching option is "invalid" — e.g. a
  // discontinued reason code still stored on the record. Surfaced as an orange
  // input (below) and by showing the raw value as the input text. Guarded on
  // options.length so a value doesn't flash orange while async options are
  // still loading (empty list).
  const isInvalidValue =
    invalidProp ||
    (flagInvalidValue &&
    !multiple &&
    !freeText &&
    !!value &&
    options.length > 0 &&
    !options.some((opt) => opt.value === value));

  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0, openUpward: false });
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isKeyboardNav = useRef(false);
  const hasInteracted = useRef(false);
  // True while the input holds focus (i.e. the user may be actively typing). The
  // value-sync effect below uses this to avoid clobbering an in-progress search
  // query when the parent hands us a fresh `options`/`value` (e.g. an async
  // option list resolving mid-type). A ref — not state — so toggling it never
  // itself triggers a render.
  const isFocused = useRef(false);

  // Update search term when value changes externally
  useEffect(() => {
    if (freeText) {
      // Free-text mode: the input mirrors `value` verbatim (the typed/picked text).
      setSearchTerm((value as string) ?? '');
    } else if (multiple) {
      // Always keep search term empty in multi-select mode
      // Selected values will be displayed as chips
      setSearchTerm('');
    } else {
      // Single-select. While the input is focused the search term IS the user's
      // in-progress query — don't overwrite it just because `options`/`value`
      // changed identity (e.g. an async option list resolving, or a parent
      // re-render). `options` must stay in the deps so a late-arriving list can
      // resolve the selected label, but that resolution waits until the field is
      // no longer focused; onFocus/handleClose reconcile the term on the way in
      // and out.
      if (isFocused.current) return;
      if (value) {
        const selectedOption = options.find(opt => opt.value === value);
        if (selectedOption) {
          setSearchTerm(selectedOption.label);
        } else if (flagInvalidValue) {
          // No matching option — show the caller-resolved label (e.g. a deleted
          // entity's name) or the raw stored value (flagged orange either way).
          setSearchTerm(invalidValueLabel || (value as string));
        }
      } else {
        setSearchTerm('');
      }
    }
  }, [value, options, multiple, flagInvalidValue, freeText, invalidValueLabel]);

  // Filter options based on search term - computed during render
  const filteredOptions = useMemo(() => {
    // `hidden` options exist only for value→label resolution (which reads the
    // full `options`) — everything display-derived starts from the visible set.
    const visible = options.some((o) => o.hidden)
      ? options.filter((o) => !o.hidden)
      : options;
    // Async free-text mode (onSearch wired) or explicit serverFiltered:
    // `options` are already server-filtered for the term, so show them as-is
    // (client-side filtering would hide city/alias/recno matches whose matched
    // column isn't rendered in the label). Free-text over a static list (no
    // onSearch) filters client-side like the fixed-option mode — nothing else
    // would ever narrow it.
    if ((freeText || serverFiltered) && onSearch) return visible;
    if (searchTerm.trim() === '') {
      return visible.some((o) => o.searchOnly) ? visible.filter((o) => !o.searchOnly) : visible;
    }
    const q = searchTerm.toLowerCase();
    return visible.filter(option =>
      option.label.toLowerCase().includes(q) ||
      option.description?.toLowerCase().includes(q) ||
      // labelSuffix carries the record # ("#1120") — typing a recno must match.
      option.labelSuffix?.toLowerCase().includes(q)
    );
  }, [searchTerm, options, freeText, serverFiltered, onSearch]);

  // Pin the selected option to the top of the list, above a divider, so the
  // current value is always the first thing you see (no scrolling to find the
  // checkmark). Single-select only — multi-select keeps its natural order so
  // checked rows don't jump as you toggle them. Skipped while searching (the
  // filtered order is what you want then) and when grouping (a group header
  // would be orphaned). `displayOptions` is the SINGLE source of truth for both
  // rendering and keyboard navigation, so the two never disagree on indices.
  const pinnedIndex =
    !multiple && !freeText && !groupBy && searchTerm.trim() === '' && !!value
      ? filteredOptions.findIndex((o) => o.value === value)
      : -1;
  const displayOptions = useMemo(() => {
    if (pinnedIndex < 0) return filteredOptions;
    const selected = filteredOptions[pinnedIndex];
    return [selected, ...filteredOptions.filter((_, i) => i !== pinnedIndex)];
  }, [filteredOptions, pinnedIndex]);
  const showPinDivider = pinnedIndex >= 0 && displayOptions.length > 1;

  // Auto-highlight the first option when filter changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchTerm, options]);

  // Mount-only focus (see the `autoFocus` prop). Goes through the ref rather than
  // the native attribute so the onFocus handler runs the same open/reset path a
  // click would — a portal-mounted modal's browser autofocus is unreliable anyway.
  useEffect(() => {
    if (autoFocus && !disabled) inputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Scroll highlighted option into view (keyboard navigation only)
  useEffect(() => {
    if (!isKeyboardNav.current) return;
    isKeyboardNav.current = false;
    if (highlightedIndex >= 0 && listRef.current) {
      const allOptions = listRef.current.querySelectorAll('button');
      const highlightedElement = allOptions[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedIndex]);

  // On open, scroll the (first) selected option into view so the current value
  // is visible without manual scrolling. With single-select pin-to-top it's
  // already at the top; this mainly covers multi-select and the non-pinned
  // cases (searching/grouped). Runs once per open (deps: isOpen).
  useEffect(() => {
    if (!isOpen) return;
    const timeoutId = setTimeout(() => {
      const listEl = listRef.current;
      if (!listEl) return;
      const selectedValues = multiple ? (Array.isArray(value) ? value : []) : [value as string];
      const idx = displayOptions.findIndex((o) => selectedValues.includes(o.value));
      if (idx < 0) return;
      const buttons = listEl.querySelectorAll('button');
      (buttons[idx] as HTMLElement | undefined)?.scrollIntoView({ block: 'nearest' });
    }, 0);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Caller-driven open (openSignal). freeText mode opens on type, so options
  // supplied by any other route (a lookup button) would otherwise never show.
  // Skips the initial mount so a component rendering a constant signal doesn't
  // pop its dropdown open on load.
  const openSignalSeen = useRef(openSignal);
  useEffect(() => {
    if (openSignal === undefined || openSignal === openSignalSeen.current) return;
    openSignalSeen.current = openSignal;
    if (disabled) return;
    setIsOpen(true);
  }, [openSignal, disabled]);

  // Calculate dropdown position when it opens and whenever it might change
  useEffect(() => {
    if (!isOpen || !containerRef.current) return;

    const updatePosition = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const dropdownMaxHeight = 320; // matches max-h-80 (80 * 4px = 320px)
        const openUpward = spaceBelow < dropdownMaxHeight && rect.top > spaceBelow;

        // The panel grows to fit its widest option (min = input width, capped at
        // MAX_PANEL_WIDTH via CSS). Measure the rendered panel so we can keep it
        // inside the viewport's right edge — a grown panel anchored at the input's
        // left would otherwise overflow for right-side columns.
        const cap = Math.min(MAX_PANEL_WIDTH, window.innerWidth - 16);
        const measured = dropdownRef.current?.getBoundingClientRect().width ?? rect.width;
        const panelWidth = Math.min(Math.max(measured, rect.width), cap);
        let left = rect.left;
        if (left + panelWidth > window.innerWidth - 8) {
          left = Math.max(8, window.innerWidth - panelWidth - 8);
        }

        setDropdownPosition({
          top: openUpward ? rect.top - 8 : rect.bottom + 8,
          left,
          width: rect.width,
          openUpward,
        });
      }
    };

    // Initial position
    updatePosition();

    // Update position on scroll or resize
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  const handleClose = useCallback(() => {
    // Closing abandons any parked Enter/Tab commit (Escape / click-away).
    pendingCommitRef.current = false;
    setIsOpen(false);
    // Reset search term to selected value
    if (freeText) {
      setSearchTerm((value as string) ?? '');
    } else if (multiple) {
      // Clear search term in multi-select mode
      setSearchTerm('');
    } else {
      if (value) {
        const selectedOption = options.find(opt => opt.value === value);
        if (selectedOption) {
          setSearchTerm(selectedOption.label);
        } else if (flagInvalidValue) {
          setSearchTerm(invalidValueLabel || (value as string));
        }
      } else {
        setSearchTerm('');
      }
    }
    onClose?.();
  }, [value, options, multiple, flagInvalidValue, freeText, onClose, invalidValueLabel]);

  // Leaving the field (click-away or Tab) with exactly ONE visible match in
  // free-text async mode commits it — "type the code, leave the field, it
  // resolves" (users expect the checkmarked lone hit to stick). Escape still
  // plain-closes (explicit cancel). A ref so the click-outside listener always
  // sees the current render's term/options without re-subscribing; assigned
  // below handleOptionSelect (it can't be referenced before it initializes).
  const commitLoneMatchOnLeave = useRef<() => boolean>(() => false);

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideContainer = containerRef.current?.contains(target);
      const isInsideDropdown = dropdownRef.current?.contains(target);

      if (!isInsideContainer && !isInsideDropdown) {
        // A committed lone match already closed + synced the term; calling
        // handleClose after it would reset the input from the stale `value`.
        if (!commitLoneMatchOnLeave.current()) handleClose();
      }
    };

    // Add listener with a small delay to avoid catching the same click that opened the dropdown
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, handleClose]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    hasInteracted.current = true;
    // New keystrokes supersede a parked Enter/Tab commit.
    pendingCommitRef.current = false;
    setSearchTerm(newValue);
    // Always open when typing
    setIsOpen(true);
    // Async mode: let the caller fetch options for the typed term.
    onSearch?.(newValue);
    // Free-text mode: the typed text IS the value — keep the caller in sync.
    if (freeText && !multiple) {
      (onChange as (value: string) => void)(newValue);
    }
  };

  const handleOptionClick = (option: TypeAheadOption) => {
    if (multiple) {
      const values = Array.isArray(value) ? value : [];
      const isSelected = values.includes(option.value);

      if (isSelected) {
        // Deselect
        const newValues = values.filter(v => v !== option.value);
        (onChange as (value: string[]) => void)(newValues);
      } else {
        // Select
        (onChange as (value: string[]) => void)([...values, option.value]);
      }
      // Keep dropdown open for multi-select
      setSearchTerm('');
    } else {
      // Free-text mode stores the display label (the picked name); fixed-option
      // mode stores the option's value/id.
      (onChange as (value: string) => void)(freeText ? option.label : option.value);
      onSelect?.(option);
      setSearchTerm(option.label);
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };

  // Enter/Tab pressed while an async search is still in flight ("gwi⏸23⏎"):
  // the visible options belong to the OLD term, so committing the highlight
  // would pick the wrong record (e.g. GWI05 from "gwi" instead of GWI23).
  // Park the intent here and commit the TOP hit of the results that land for
  // the final term. Canceled by further typing, Escape, close, or clear.
  const pendingCommitRef = useRef(false);
  const prevSearchingRef = useRef(searching);
  useEffect(() => {
    const wasSearching = prevSearchingRef.current;
    prevSearchingRef.current = searching;
    if (!wasSearching || searching) return; // only on in-flight → settled
    if (!pendingCommitRef.current) return;
    pendingCommitRef.current = false;
    if (displayOptions.length > 0) handleOptionClick(displayOptions[0]);
    // handleOptionClick is stable-enough (plain closure) — rerunning on its
    // identity would fire commits on unrelated renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searching, displayOptions]);

  const handleOptionSelect = (option: TypeAheadOption, shouldBlur: boolean = true) => {
    if (multiple) {
      const values = Array.isArray(value) ? value : [];
      const isSelected = values.includes(option.value);

      if (isSelected) {
        (onChange as (value: string[]) => void)(values.filter(v => v !== option.value));
      } else {
        (onChange as (value: string[]) => void)([...values, option.value]);
      }
      setSearchTerm('');
    } else {
      (onChange as (value: string) => void)(freeText ? option.label : option.value);
      onSelect?.(option);
      setSearchTerm(option.label);
      setIsOpen(false);
      if (shouldBlur) {
        inputRef.current?.blur();
      }
    }
  };

  commitLoneMatchOnLeave.current = () => {
    if (multiple || !freeText) return false;
    if (searchTerm.trim() === '' || displayOptions.length !== 1) return false;
    const lone = displayOptions[0];
    if (lone.label === searchTerm) return false; // already committed
    handleOptionSelect(lone, false);
    return true;
  };

  // Muted "#id" adornment inside the field's right edge while a value shows and
  // the dropdown is closed — never part of the input text, so styled gray and
  // unambiguous next to names that themselves contain "#..." (store numbers).
  // Fixed-option mode derives it from the selected option; free-text callers
  // pass `valueSuffix`. Sits just left of the chevron/clear controls; the input
  // reserves matching padding so long values never run underneath it.
  const closedSuffix =
    showValueSuffix && !multiple && !isOpen && searchTerm !== ''
      ? (freeText ? valueSuffix : options.find((o) => o.value === value)?.labelSuffix)
      : undefined;
  const controlsRem = allowClear ? (compact ? 3.5 : 5) : (compact ? 2 : 3);

  // The name always wins over the id: only show the adornment when the value
  // text fits beside it — a narrow field shows the full name instead of
  // "Cn Railroad - … #31091" truncation (the id is still in the dropdown /
  // the caller's under-field info line). Canvas-measured with the input's own
  // font; environments without canvas 2D (jsdom) keep the adornment.
  const [suffixFits, setSuffixFits] = useState(true);
  useLayoutEffect(() => {
    if (!closedSuffix) return;
    const el = inputRef.current;
    if (!el || el.clientWidth === 0) { setSuffixFits(true); return; }
    const ctx = getMeasureContext();
    if (!ctx) { setSuffixFits(true); return; }
    const cs = window.getComputedStyle(el);
    ctx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
    const available =
      el.clientWidth - (parseFloat(cs.paddingLeft) || 0) - controlsRem * 16
      - ctx.measureText(closedSuffix).width - 8; // 8px name↔id gap
    setSuffixFits(ctx.measureText(searchTerm).width <= available);
  }, [closedSuffix, searchTerm, controlsRem]);
  const showClosedSuffix = !!closedSuffix && suffixFits;

  const handleClear = () => {
    pendingCommitRef.current = false;
    if (multiple) {
      (onChange as (value: string[]) => void)([]);
    } else {
      (onChange as (value: string) => void)('');
    }
    setSearchTerm('');
    // Async callers key their result list off the term — tell them it's gone,
    // or reopening the dropdown would surface the previous search's results.
    onSearch?.('');
    setIsOpen(false);
    inputRef.current?.blur();
    onClear?.();
  };

  const handleRemoveValue = (valueToRemove: string) => {
    if (multiple) {
      const values = Array.isArray(value) ? value : [];
      (onChange as (value: string[]) => void)(values.filter(v => v !== valueToRemove));
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        isKeyboardNav.current = true;
        hasInteracted.current = true;
        setHighlightedIndex(prev =>
          prev < displayOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        isKeyboardNav.current = true;
        hasInteracted.current = true;
        setHighlightedIndex(prev =>
          prev > 0 ? prev - 1 : displayOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        // Search in flight → the visible highlight is for the PREVIOUS term.
        // Defer: the top hit of the incoming results commits instead.
        if (onSearch && searching && !multiple) {
          pendingCommitRef.current = true;
          break;
        }
        if (highlightedIndex >= 0 && highlightedIndex < displayOptions.length) {
          handleOptionClick(displayOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        // Consume the key: with the dropdown open, Escape means "close the
        // dropdown", not "close the enclosing drawer" — without this it
        // bubbles to ProDetailSlideOver's window-level Escape handler and
        // tries to close the whole order. A second Escape (dropdown closed,
        // handled by the !isOpen branch above) still reaches the drawer.
        e.stopPropagation();
        handleClose();
        break;
      case 'Tab':
        // Same stale-results hazard as Enter — defer the commit, let focus move.
        if (onSearch && searching && !multiple && hasInteracted.current) {
          pendingCommitRef.current = true;
          break;
        }
        // Only confirm a selection on Tab if user actively typed or navigated
        if (hasInteracted.current && highlightedIndex >= 0 && highlightedIndex < displayOptions.length) {
          handleOptionSelect(displayOptions[highlightedIndex], false);
        } else if (displayOptions.length === 1 && searchTerm.trim() !== '') {
          // A lone visible match commits on Tab even without typing/arrow-nav
          // since the last focus — e.g. the field was refocused (which resets
          // hasInteracted) with its single search hit still showing.
          handleOptionSelect(displayOptions[0], false);
        } else {
          handleClose();
        }
        break;
    }
  };

  // Group options if groupBy is enabled. (When grouping, `pinnedIndex` is -1 so
  // `displayOptions === filteredOptions` — pin-to-top and grouping never mix.)
  const groupedOptions = groupBy
    ? displayOptions.reduce((acc, option) => {
        const group = option.group || 'Other';
        if (!acc[group]) acc[group] = [];
        acc[group].push(option);
        return acc;
      }, {} as Record<string, TypeAheadOption[]>)
    : { '': displayOptions };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-label mb-2">
          {label}
        </label>
      )}

      <div className={chipsDirection === 'down' ? 'flex flex-col gap-2' : 'flex gap-2 items-center'}>
        <div className={`relative ${chipsDirection === 'down' ? 'w-full' : 'flex-1'}`}>
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={(event) => {
              if (!disabled) {
                isFocused.current = true; // Guard the value-sync effect from wiping typing
                hasInteracted.current = false; // Reset — user hasn't typed or navigated yet
                // Free-text mode opens on type (not focus) and keeps `value`
                // shown; clearing here would blank it + flash "No results".
                if (freeText) return;
                if (preserveValueOnFocus && !multiple) {
                  event.currentTarget.select();
                } else {
                  setSearchTerm(''); // Clear to show all options when opening
                }
                setIsOpen(true);
              }
            }}
            onBlur={() => {
              // Once the field loses focus the value-sync effect may reconcile
              // the search term back to the selected label again.
              isFocused.current = false;
            }}
            placeholder={placeholder}
            aria-label={ariaLabel}
            disabled={disabled}
            autoComplete="off"
            className={`w-full ${compact ? 'px-2 py-1' : 'px-4 py-2.5'} ${showSearch && !plainLocked ? (compact ? 'pl-7' : 'pl-10') : ''} ${
              // Right padding reserves room for the chevron + the clear ✕; a
              // non-clearable field never shows the ✕, so reclaim that space
              // for text (narrow grid cells were clipping values like 'ALL').
              allowClear ? (compact ? 'pr-14' : 'pr-20') : (compact ? 'pr-8' : 'pr-12')
            } border rounded-lg ${compact ? 'text-xs' : 'text-sm'} text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:border-transparent transition-all ${
              isInvalidValue
                ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-400 dark:border-orange-500 focus:ring-orange-500'
                : 'bg-panel border-gray-300 dark:border-gray-600 focus:ring-indigo-500'
            } ${
              disabled ? 'opacity-50 cursor-not-allowed' : ''
            } ${shellClass}`}
            style={showClosedSuffix
              ? { paddingRight: `calc(${controlsRem}rem + ${closedSuffix!.length + 1}ch)` }
              : undefined}
          />

          {showClosedSuffix && (
            <span
              className={`absolute top-1/2 -translate-y-1/2 pointer-events-none ${compact ? 'text-xs' : 'text-sm'} text-gray-400 dark:text-gray-500`}
              style={{ right: `${controlsRem}rem` }}
            >
              {closedSuffix}
            </span>
          )}

          {showSearch && !plainLocked && (
            <div className={`absolute inset-y-0 left-0 flex items-center ${compact ? 'pl-2' : 'pl-3'} pointer-events-none`}>
              <MagnifyingGlassIcon className={compact ? 'h-3.5 w-3.5 text-gray-400' : 'h-5 w-5 text-gray-400'} />
            </div>
          )}

          {!plainLocked && (
          <div className={`absolute inset-y-0 right-0 flex items-center ${compact ? 'pr-1.5 gap-0.5' : 'pr-2 gap-1'}`}>
            {allowClear && (searchTerm || value) && !disabled && (
              <button
                onClick={handleClear}
                type="button"
                tabIndex={-1}
                className={`${compact ? 'p-0.5' : 'p-1.5'} hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors`}
                title="Clear"
              >
                <XMarkIcon className={compact ? 'h-3 w-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300' : 'h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'} />
              </button>
            )}
            <ChevronUpDownIcon
              className={`${compact ? 'h-3.5 w-3.5' : 'h-5 w-5'} text-gray-400 transition-transform ${
                isOpen ? 'transform rotate-180' : ''
              }`}
            />
          </div>
          )}
        </div>

        {/* Selected values chips for multi-select */}
        {multiple && Array.isArray(value) && value.length > 0 && (
          <div className={`flex gap-1.5 ${chipsDirection === 'down' ? 'flex-wrap' : 'items-center flex-shrink-0'}`}>
            {(chipsDirection === 'down' ? value : value.slice(0, 3)).map(val => {
              const option = options.find(opt => opt.value === val);
              return (
                <span
                  key={val}
                  className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300 text-xs rounded whitespace-nowrap"
                >
                  {option?.label || val}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveValue(val);
                    }}
                    className="hover:bg-indigo-200 dark:hover:bg-indigo-800/50 rounded-full p-0.5 transition-colors"
                    disabled={disabled}
                  >
                    <XMarkIcon className="h-3 w-3" />
                  </button>
                </span>
              );
            })}
            {chipsDirection === 'right' && value.length > 3 && (
              <span className="inline-flex items-center px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-label text-xs rounded whitespace-nowrap">
                +{value.length - 3}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && createPortal(
        <div
          ref={dropdownRef}
          className={`fixed z-[99999] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl ${maxHeight} overflow-auto`}
          style={{
            ...(dropdownPosition.openUpward
              ? { bottom: `${window.innerHeight - dropdownPosition.top}px` }
              : { top: `${dropdownPosition.top}px` }),
            left: `${dropdownPosition.left}px`,
            // Shrink-to-fit the widest option (no explicit `width`), but never
            // narrower than the input and never past the viewport / MAX cap —
            // so long values are no longer truncated by the input's width.
            minWidth: `${dropdownPosition.width}px`,
            maxWidth: `min(${MAX_PANEL_WIDTH}px, calc(100vw - 16px))`,
          }}
        >
          {displayOptions.length > 0 ? (
            <div ref={listRef} className="py-2">
              {Object.entries(groupedOptions).map(([group, groupOptions]) => (
                <div key={group}>
                  {groupBy && group && (
                    <div className="px-3 py-2 text-xs font-semibold text-muted uppercase tracking-wider bg-gray-50 dark:bg-gray-900/50 sticky top-0">
                      {group}
                    </div>
                  )}
                  {groupOptions.map((option, index) => {
                    const globalIndex = displayOptions.indexOf(option);
                    const isHighlighted = globalIndex === highlightedIndex;
                    const isSelected = multiple
                      ? (Array.isArray(value) ? value : []).includes(option.value)
                      : value === option.value;

                    const custom = renderOption?.(option);
                    return (
                      <React.Fragment key={`${option.value}|${option.description ?? ''}`}>
                        {/* Divider separating the pinned selected option (index 0)
                            from the rest of the list. */}
                        {showPinDivider && globalIndex === 1 && (
                          <div role="separator" className="my-1 border-t border-gray-200 dark:border-gray-700" />
                        )}
                        <button
                          onClick={() => handleOptionClick(option)}
                          onMouseEnter={() => setHighlightedIndex(globalIndex)}
                          className={`w-full text-left px-4 py-2.5 flex items-start gap-3 transition-colors ${
                            isHighlighted
                              ? 'bg-indigo-50 dark:bg-indigo-900/20'
                              : isSelected
                              ? 'bg-gray-50 dark:bg-gray-700/50'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                          }`}
                        >
                        {custom ? (
                          <div className="flex-1 min-w-0">{custom}</div>
                        ) : (
                          <>
                            {showIcons && option.icon && (
                              <div className={`flex-shrink-0 mt-0.5 ${
                                isHighlighted ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400'
                              }`}>
                                {option.icon}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-baseline gap-1.5 min-w-0">
                                <OptionLabel
                                  label={option.label}
                                  className={`text-sm font-medium truncate min-w-0 ${
                                    isHighlighted
                                      ? 'text-indigo-900 dark:text-indigo-100'
                                      : isSelected
                                      ? 'text-body'
                                      : 'text-label'
                                  }`}
                                />
                                {option.labelSuffix && (
                                  <span className="flex-shrink-0 text-xs text-gray-400 dark:text-gray-500">
                                    {option.labelSuffix}
                                  </span>
                                )}
                              </div>
                              {showDescriptions && option.description && (
                                <div className={`text-xs mt-0.5 ${
                                  isHighlighted
                                    ? 'text-indigo-700 dark:text-indigo-300'
                                    : 'text-muted'
                                }`}>
                                  {option.description}
                                </div>
                              )}
                            </div>
                            {isSelected && (
                              <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-indigo-600 dark:text-indigo-400" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                              </div>
                            )}
                          </>
                        )}
                        </button>
                      </React.Fragment>
                    );
                  })}
                </div>
              ))}
            </div>
          ) : searching ? (
            <div className="px-4 py-8 text-center">
              <div className="mx-auto h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <p className="mt-3 text-sm font-medium text-label">Searching…</p>
            </div>
          ) : emptyMessage ? (
            <div className="px-4 py-6 text-center">
              <p className="text-sm text-muted">{emptyMessage}</p>
              {/* Keep the create action available pre-search (e.g. "Add As New
                  Customer" on the quote wizard before 2 characters are typed). */}
              {onCreateNew && (
                <button
                  onMouseDown={e => { e.preventDefault(); onCreateNew(searchTerm); handleClose(); }}
                  className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-md transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                  {createNewLabel ? createNewLabel(searchTerm) : `Add "${searchTerm || '...'}"`}
                </button>
              )}
            </div>
          ) : (
            <div className="px-4 py-8 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="mt-2 text-sm font-medium text-body">No results found</p>
              <p className="mt-1 text-sm text-muted">
                No options match "<span className="font-medium">{searchTerm}</span>"
              </p>
              {onCreateNew && (
                <button
                  onMouseDown={e => { e.preventDefault(); onCreateNew(searchTerm); handleClose(); }}
                  className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-md transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                  {createNewLabel ? createNewLabel(searchTerm) : `Add "${searchTerm || '...'}"`}
                </button>
              )}
            </div>
          )}
          {onCreateNew && displayOptions.length > 0 && (
            <div className="border-t border-gray-100 dark:border-gray-700 py-1">
              <button
                onMouseDown={e => { e.preventDefault(); onCreateNew(searchTerm); handleClose(); }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                </svg>
                {createNewLabel ? createNewLabel(searchTerm) : (searchTerm ? `Add "${searchTerm}"` : 'Add new...')}
              </button>
            </div>
          )}
        </div>,
        // When inside a modal <dialog> (opened via showModal), portal into that
        // dialog so the dropdown shares its top-layer stacking context —
        // otherwise it renders beneath the dialog regardless of z-index. An
        // explicit portalContainer always wins; falls back to document.body.
        portalContainer || containerRef.current?.closest('dialog') || document.body
      )}
    </div>
  );
};

export default TypeAhead;
